/* eslint-disable @typescript-eslint/no-explicit-any */
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { toast } from "components/ui/use-toast";
import { useCallback, useEffect, useRef } from "react";
import { MdOutlineFileUpload } from "react-icons/md";
import {
  useGetFilesQuery,
  usePublicGetFilesQuery,
  usePublicUploadFileMutation,
  useUploadFileMutation,
} from "../../../../../services/file";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { featureFlags } from "@/config/feature-flag";

interface FormData {
  evidence: FileList;
}

interface EvidenceCellProps {
  row: any;
  entity_name: string;
  methods: any;
  globalIsUploading: boolean;
  setGlobalIsUploading: (isUploading: boolean) => void;
}

export default function EvidenceCell({
  row,
  entity_name,
  methods,
  globalIsUploading,
  setGlobalIsUploading,
}: EvidenceCellProps) {
  const uploadFileMutationPrivate = useUploadFileMutation();
  const uploadFileMutationPublic = usePublicUploadFileMutation();

  const featureFlagsChoice = featureFlags.USE_KEYCLOAK;

  const [uploadFile, { isLoading: localIsLoading }] = featureFlagsChoice
    ? uploadFileMutationPublic
    : uploadFileMutationPrivate;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { register, handleSubmit, setValue } = methods;
  const nsu = row.original;
  const { validated } = nsu;
  useEffect(() => {
    if (localIsLoading) {
      setGlobalIsUploading(true);
    }
  }, [localIsLoading, setGlobalIsUploading]);

  const rowId = row?.original?.id;
  const objectGetFiles = {
    entity_name: entity_name ?? "",
    id: rowId?.toString() ?? "",
  };

  const getFileKeycloak = featureFlagsChoice
    ? usePublicGetFilesQuery(objectGetFiles)
    : useGetFilesQuery(objectGetFiles);
  const { data: response } = getFileKeycloak;

  const filesData = response?.data ?? [];
  const filesCount = filesData.length;

  setValue(`nsu_opr.length_file_${rowId}`, filesCount);
  const onSubmit = async (data: FormData) => {
    try {
      if (data.evidence && data.evidence.length > 0) {
        const file = data.evidence[0];

        console.log("Starting upload for ID:", rowId);

        await uploadFile({
          file,
          entity_name,
          entity_id: row.original.id as number,
          audit: true,
        }).unwrap();

        console.log("Upload completed for ID:", rowId);

        toast({
          variant: "default",
          description: "Evidência enviada com sucesso",
          duration: 5000,
        });

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        setTimeout(() => {
          console.log("Forcing global state reset");
          setGlobalIsUploading(false);
        }, 500);
      }
    } catch (error) {
      console.error("Failed to upload file:", error);
      toast({
        variant: "destructive",
        description: "Erro ao realizar ação",
        duration: 5000,
      });

      setGlobalIsUploading(false);
    }
  };
  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      console.log("File change triggered", { validated, globalIsUploading });
      if (globalIsUploading) {
        console.log("File change prevented due to global upload in progress");
        return;
      }

      if (e.target.files && e.target.files.length > 0) {
        console.log("Processing file upload");
        setValue("evidence", e.target.files);
        setGlobalIsUploading(true);
        handleSubmit(onSubmit)();
      }
    },
    [
      validated,
      globalIsUploading,
      setValue,
      setGlobalIsUploading,
      handleSubmit,
      onSubmit,
    ]
  );

  const triggerFileInput = useCallback(() => {
    console.log("Trigger file input", {
      globalIsUploading,
      localIsLoading,
      validated,
    });
    if (globalIsUploading || localIsLoading) {
      console.log("File input trigger prevented due to upload in progress");
      return;
    }

    if (fileInputRef.current) {
      console.log("Opening file dialog");
      fileInputRef.current.click();
    }
  }, [globalIsUploading, localIsLoading]);
  const isCurrentlyUploading = localIsLoading;
  const isOtherUploading = globalIsUploading && !localIsLoading;
  const isDisabled = isCurrentlyUploading || isOtherUploading;

  return (
    <Tooltip>
      <TooltipTrigger
        onClick={triggerFileInput}
        className={`w-full h-full px-[10px] py-[6px] flex items-center justify-center gap-4 ${
          isDisabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
        disabled={isDisabled}
      >
        <div className="w-full h-full px-[10px] py-[6px] flex items-center justify-center gap-4">
          {filesCount > 0 && (
            <span className="truncate overflow-hidden whitespace-nowrap underline cursor-pointer">
              {filesData
                .slice(0, 3)
                .map((file: any) => file?.file_name ?? "Arquivo")
                .join(", ")}{" "}
              <span className="text-[#74788D]">...</span>
            </span>
          )}
          <div>
            {isCurrentlyUploading ? (
              <AiOutlineLoading3Quarters className="text-black animate-spin !text-[16px] font-bold" />
            ) : isOtherUploading ? (
              <AiOutlineLoading3Quarters className="text-gray-400 animate-spin !text-[16px] font-bold" />
            ) : (
              <MdOutlineFileUpload className="text-black cursor-pointer !text-[16px] font-bold" />
            )}
            <form onSubmit={handleSubmit(onSubmit)} className="hidden">
              <input
                type="file"
                {...register("evidence")}
                onChange={handleFileChange}
                ref={fileInputRef}
                className="hidden"
                disabled={globalIsUploading}
              />
            </form>
          </div>
        </div>
      </TooltipTrigger>
      {filesCount > 0 && (
        <TooltipContent className="bg-black text-white text-center">
          <span>{filesData.map((file: any) => file?.file_name ?? "Arquivo").join(", ")}</span>
        </TooltipContent>
      )}
    </Tooltip>
  );
}
